import React, { useState, useEffect, useCallback, useMemo } from 'react';
// import { EditProductIQModal } from "components/supplier-dynamics/SupplierCard";
import {
  Layout,
  Card,
  Button,
  Typography,
  Space,
  Table,
  Tooltip,
  Select,
  InputNumber,
  Form,
  Checkbox,
  Modal,
  message,
  Pagination
} from 'antd';
import {
  ReloadOutlined,
  CheckCircleFilled,
  AppstoreOutlined,
  BankOutlined,
  WarningFilled,
  WarningOutlined,
  SettingOutlined,
  SearchOutlined,
  SaveFilled,
  SettingFilled,
  FileOutlined,
  InfoCircleOutlined,
  FileTextOutlined,
  ExportOutlined,
  RobotOutlined,
  UpOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import DownloadIcon from '../../assets/download-icon.svg';
import './SiteAdmin.scss';
import { useVendorApi, DataRefreshJob, DataRefreshJobStatusResponse, PaginationInfo, FailedVendor } from 'api/vendor';
import { SupplierDynamicsVendor } from 'types/global';
import { CustomScanModal, SupplierOption } from 'components/modals/SupplierConfigurationCard';

const { Sider, Content } = Layout;
const { Title, Text } = Typography;
const { Option } = Select;

type Row = {
  key: string;
  id?: string;
  startTime: string;
  completedAt?: string;
  status?: string;
  syncType?: 'SUPPLIER_IQ' | 'SUPPLIER_RISK' | 'PRODUCT_IQ';
  successfulVendors?: number;
  failedVendors?: number;
  createdBy?: string;
  createdByName?: string;
  failedVendorDetails?: Array<{ id: number; name: string; vendor_domain?: string }>;
  completedProducts?: number;
  failedProducts?: number;
  // Legacy fields for backward compatibility
  supplierOk?: number;
  supplierAlerts?: number;
  productOk?: number;
  productAlerts?: number;
  type?: string;
  reports?: string;
};

type ContractFilterSettings = {
  operator: 'gte' | 'lte';
  contractCount: number;
  timeValue: number;
  timeUnit: 'Days' | 'Weeks' | 'Months' | 'Years';
};

const DEFAULT_CONTRACT_FILTER_SETTINGS: ContractFilterSettings = {
  operator: 'gte',
  contractCount: 5,
  timeValue: 6,
  timeUnit: 'Months'
};

/**
 * Convert time value and unit to months
 * @param timeValue - The raw time value input
 * @param timeUnit - The unit the user selected
 * @returns Number of months represented by the provided value/unit pair
 */
const convertToMonths = (timeValue: number, timeUnit: 'Days' | 'Weeks' | 'Months' | 'Years'): number => {
  switch (timeUnit) {
    case 'Days':
      return Math.round((timeValue / 30) * 100) / 100;
    case 'Weeks':
      return Math.round((timeValue / 4.33) * 100) / 100;
    case 'Months':
      return timeValue;
    case 'Years':
      return timeValue * 12;
    default:
      return timeValue;
  }
};

const formatDateTime = (value?: string): string => {
  const options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  };

  if (!value) {
    return new Date().toLocaleString('en-US', options);
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return parsedDate.toLocaleString('en-US', options);
};

const mergeJobIdLists = (existing: string[], incoming: string[]): string[] => {
  if (!incoming || incoming.length === 0) {
    return existing;
  }
  const merged: string[] = [];
  [...incoming, ...existing].forEach((id) => {
    if (id && !merged.includes(id)) {
      merged.push(id);
    }
  });
  return merged;
};

const extractJobIdsFromResponse = (response: unknown): string[] => {
  const ids: string[] = [];

  const visit = (value: unknown) => {
    if (!value) return;

    if (typeof value === 'string') {
      ids.push(value);
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => visit(item));
      return;
    }

    if (typeof value === 'object') {
      const obj = value as Record<string, unknown>;
      ['id', 'jobId'].forEach((key) => {
        const val = obj[key];
        if (typeof val === 'string') {
          ids.push(val);
        }
      });

      ['jobIds', 'ids'].forEach((key) => {
        const val = obj[key];
        if (Array.isArray(val)) {
          val.forEach((item) => visit(item));
        }
      });

      if ('data' in obj) {
        visit(obj.data);
      }
    }
  };

  visit(response);
  return Array.from(new Set(ids.filter(Boolean)));
};

const getSuccessfulVendorData = (status?: DataRefreshJobStatusResponse): { value: number | undefined } => {
  if (!status) {
    return { value: undefined };
  }

  if (typeof status.successfulVendors === 'number') {
    return { value: status.successfulVendors };
  }

  if (typeof status.completedVendors === 'number') {
    return { value: status.completedVendors };
  }

  return { value: undefined };
};

const getFailedVendorData = (
  status?: DataRefreshJobStatusResponse,
  successfulValue?: number
): { value: number | undefined } => {
  if (!status) {
    return { value: undefined };
  }

  if (typeof status.failedVendors === 'number') {
    return { value: status.failedVendors };
  }

  if (typeof status.totalVendors === 'number') {
    const successCount =
      typeof successfulValue === 'number' ? successfulValue : getSuccessfulVendorData(status).value ?? 0;
    return { value: Math.max(status.totalVendors - successCount, 0) };
  }

  return { value: undefined };
};

const mapStatusToRow = (
  status: DataRefreshJobStatusResponse,
  fallbackSyncType?: 'SUPPLIER_IQ' | 'SUPPLIER_RISK' | 'PRODUCT_IQ'
): Row => {
  const successfulInfo = getSuccessfulVendorData(status);
  const failedInfo = getFailedVendorData(status, successfulInfo.value);
  const successfulCount = successfulInfo.value ?? 0;
  const failedCount = failedInfo.value ?? 0;

  return {
    key: status.id || `job-${Date.now()}`,
    id: status.id,
    startTime: formatDateTime(status.startTime),
    completedAt: status.completedAt,
    status: status.status,
    syncType: status.syncType ?? fallbackSyncType,
    successfulVendors: successfulCount,
    failedVendors: failedCount,
    createdBy: status.createdBy,
    createdByName: status.createdByName,
    failedVendorDetails: status.failedVendorDetails ?? [],
    completedProducts: status.completedProducts ?? 0,
    failedProducts: status.failedProducts ?? 0,
    supplierOk: successfulCount,
    supplierAlerts: failedCount,
    productOk: status.completedProducts ?? 0,
    productAlerts: status.failedProducts ?? 0,
    type: '',
    reports: status.syncType ?? fallbackSyncType ?? ''
  };
};

const getColumns = (onRowClick: (record: Row) => void, onFailedVendorsClick?: (record: Row) => void) => [
  {
    title: 'Start Time',
    dataIndex: 'startTime',
    key: 'startTime',
    render: (val: string) => <Text>{val}</Text>
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    render: (status: string) => {
      let color = 'default';
      let icon = null;

      if (status === 'PROCESSING') {
        color = '#1890ff';
        icon = <LoadingOutlined spin />;
      } else if (status === 'CANCELLED') {
        color = '#8c8c8c';
      } else if (status === 'FAILED') {
        color = '#ff4d4f';
      } else if (status === 'COMPLETED') {
        color = '#52c41a';
      }

      return (
        <Space>
          {icon}
          <Text style={{ color }}>{status}</Text>
        </Space>
      );
    }
  },
  {
    title: 'Supplier Data',
    key: 'supplier',
    render: (_: any, record: Row) => {
      const successfulCount = record.successfulVendors ?? record.supplierOk ?? 0;
      const failedCount = record.failedVendors ?? record.supplierAlerts ?? 0;

      return (
        <Space size={16} className="flex-wrap">
          <Space>
            <CheckCircleFilled style={{ color: '#28a745' }} />
            <Text strong style={{ color: '#28a745' }}>
              {successfulCount.toLocaleString()}
            </Text>
          </Space>
          {failedCount > 0 && onFailedVendorsClick ? (
            <Space
              className="cursor-pointer hover:opacity-80"
              onClick={(e) => {
                e.stopPropagation();
                onFailedVendorsClick(record);
              }}
            >
              <WarningFilled style={{ color: '#EC4E4E' }} />
              <Text style={{ color: '#ff4d4f' }}>{failedCount}</Text>
            </Space>
          ) : (
            <Space>
              <WarningFilled style={{ color: '#EC4E4E' }} />
              <Text style={{ color: '#ff4d4f' }}>{failedCount}</Text>
            </Space>
          )}
        </Space>
      );
    }
  },
  {
    title: 'Product',
    key: 'product',
    render: (_: any, record: Row) => {
      const completedCount = record.completedProducts ?? record.productOk ?? 0;
      const failedCount = record.failedProducts ?? record.productAlerts ?? 0;

      return (
        <Space size={16} className="flex-wrap">
          <Space>
            <CheckCircleFilled style={{ color: '#28a745' }} />
            <Text strong style={{ color: '#28a745' }}>
              {completedCount.toLocaleString()}
            </Text>
          </Space>
          <Space>
            <WarningFilled style={{ color: '#EC4E4E' }} />
            <Text style={{ color: '#ff4d4f' }}>{failedCount}</Text>
          </Space>
        </Space>
      );
    }
  },
  {
    title: 'Reports',
    key: 'reports',
    render: (_: any, record: Row) => <Text>{record.createdByName || record.reports || '-'}</Text>
  }
];

/**
 *
 */
export default function IQDashboard(): JSX.Element {
  const [activeTab, setActiveTab] = useState<'product' | 'supplier' | 'supplierRisk' | 'exportReport'>('product');
  const [form] = Form.useForm();

  const [productIQJobs, setProductIQJobs] = useState<Row[]>([]);
  const [currentScanJobIds, setCurrentScanJobIds] = useState<string[]>([]);
  const [paginationInfo, setPaginationInfo] = useState<PaginationInfo | null>(null);
  const [loadingJobs, setLoadingJobs] = useState<boolean>(false);

  const currentData =
    activeTab === 'product' ? productIQJobs : activeTab === 'supplier' ? [] : activeTab === 'supplierRisk' ? [] : [];
  const [showSettings, setShowSettings] = useState(false);
  const [vendors, setVendors] = useState<SupplierDynamicsVendor[]>([]);
  const [loadingVendors, setLoadingVendors] = useState<boolean>(false);
  const [productIQEnabled, setProductIQEnabled] = useState<boolean>(false);
  const [isScanning, setIsScanning] = useState<boolean>(false);
  const [scannedSuppliers, setScannedSuppliers] = useState<number>(0);
  const [collectedProducts, setCollectedProducts] = useState<number>(0);
  const [isCustomScanModalOpen, setIsCustomScanModalOpen] = useState<boolean>(false);
  const [customScanSuppliers, setCustomScanSuppliers] = useState<SupplierOption[]>([]);
  const [filteredVendors, setFilteredVendors] = useState<
    Array<{ id: number; vendor_name: string; vendor_domain: string }>
  >([]);
  const [loadingFilteredVendors, setLoadingFilteredVendors] = useState<boolean>(false);
  const filteredVendorOptions = useMemo<SupplierOption[]>(
    () =>
      filteredVendors.map((vendor) => ({
        id: vendor.id,
        name: vendor.vendor_name,
        vendor_domain: vendor.vendor_domain
      })),
    [filteredVendors]
  );
  const [settingsFormValues, setSettingsFormValues] = useState<ContractFilterSettings>(() => ({
    ...DEFAULT_CONTRACT_FILTER_SETTINGS
  }));
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [isStartingScan, setIsStartingScan] = useState<boolean>(false);
  const [jobIdsForPolling, setJobIdsForPolling] = useState<string[]>([]);
  const [settingsLoaded, setSettingsLoaded] = useState<boolean>(false);

  // API
  const {
    filterVendorsByContracts,
    fetchSDSuppliers,
    startVendorDataRefresh,
    getDataRefreshJobsStatus,
    getDataRefreshJobs,
    cancelDataRefreshJob,
    getAiConfigSettings,
    saveAiConfigSettings
  } = useVendorApi();

  // Load AI config settings from API on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settings = await getAiConfigSettings();
        if (settings) {
          setSettingsFormValues({
            operator: settings.operator,
            contractCount: settings.contractCount,
            timeValue: settings.timeValue,
            timeUnit: settings.timeUnit
          });
        }
      } catch (error) {
        message.error('Failed to load AI config settings');
        // Keep default settings on error
      } finally {
        setSettingsLoaded(true);
      }
    };
    loadSettings();
  }, []);

  /**
   * Fetch vendors based on the provided contract filter settings.
   * @param config - Filter configuration that determines which vendors are returned
   * @returns Promise that resolves to true when at least one vendor is returned, false otherwise
   * @throws Error when the API call fails
   */
  const applyContractFilters = useCallback(async (config: ContractFilterSettings): Promise<boolean> => {
    try {
      setLoadingFilteredVendors(true);
      const months = convertToMonths(config.timeValue, config.timeUnit);
      const vendorsList = await filterVendorsByContracts({
        operator: config.operator,
        months,
        contractCount: config.contractCount
      });

      if (vendorsList && Array.isArray(vendorsList)) {
        setFilteredVendors(vendorsList);
        return vendorsList.length > 0;
      }

      setFilteredVendors([]);
      return false;
    } catch (error) {
      setFilteredVendors([]);
      throw error;
    } finally {
      setLoadingFilteredVendors(false);
    }
  }, []);

  /**
   * Fetch data refresh jobs for Product IQ tab
   * @param page
   * @param sizeOverride
   */
  const fetchProductIQJobs = async (page = 1, sizeOverride?: number) => {
    if (activeTab !== 'product') return;

    try {
      setLoadingJobs(true);
      const effectiveSize = sizeOverride ?? pageSize;
      const response = await getDataRefreshJobs({
        page,
        size: effectiveSize,
        syncType: 'PRODUCT_IQ'
      });

      if (response?.pagination && response?.content) {
        setPaginationInfo({
          ...response.pagination,
          pageSize: effectiveSize
        });

        // Transform API response to Row format
        // Handle both nested array (DataRefreshJob[][]) and flat array (DataRefreshJob[])
        let jobs: DataRefreshJob[] = [];

        if (Array.isArray(response.content)) {
          // Check if first element is an array (nested) or object (flat)
          if (response.content.length > 0 && Array.isArray(response.content[0])) {
            // Nested array: [[job1, job2], [job3]]
            jobs = response.content.flat();
          } else {
            // Flat array: [job1, job2, job3]
            jobs = response.content as DataRefreshJob[];
          }
        }

        const rows: Row[] = jobs.map((job, index) => {
          const successfulCount =
            typeof job.successfulVendors === 'number'
              ? job.successfulVendors
              : typeof (job as any).completedVendors === 'number'
              ? (job as any).completedVendors
              : 0;

          const failedCount =
            typeof job.failedVendors === 'number'
              ? job.failedVendors
              : typeof (job as any).totalVendors === 'number'
              ? Math.max((job as any).totalVendors - successfulCount, 0)
              : 0;

          return {
            key: job.id || `job-${index}`,
            id: job.id,
            startTime: job.startTime
              ? new Date(job.startTime).toLocaleString('en-US', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })
              : '',
            completedAt: job.completedAt,
            status: job.status,
            syncType: job.syncType,
            successfulVendors: successfulCount,
            failedVendors: failedCount,
            createdBy: job.createdBy,
            createdByName: job.createdByName,
            failedVendorDetails: job.failedVendorDetails || [],
            completedProducts: (job as any).completedProducts ?? 0,
            failedProducts: (job as any).failedProducts ?? 0,
            // Map to legacy fields for display
            supplierOk: successfulCount,
            supplierAlerts: failedCount,
            productOk: (job as any).completedProducts ?? 0,
            productAlerts: (job as any).failedProducts ?? 0,
            type: '', // Empty for now as per requirements
            reports: job.syncType || '' // Show syncType in reports column
          };
        });

        // Extract and store job IDs for polling
        const ids = jobs.filter((job) => job.id).map((job) => job.id as string);
        setJobIdsForPolling((prevIds) => mergeJobIdLists(prevIds, ids));

        setProductIQJobs(rows);
      } else {
        setProductIQJobs([]);
      }
    } catch (err) {
      message.error('Failed to load jobs data');
    } finally {
      setLoadingJobs(false);
    }
  };

  /**
   * Fetch vendors list when export report tab is active
   */
  useEffect(() => {
    if (activeTab === 'exportReport') {
      const fetchVendors = async () => {
        try {
          setLoadingVendors(true);
          const vendorsList = await fetchSDSuppliers();
          if (vendorsList) {
            setVendors(vendorsList);
          }
        } catch (err) {
          message.error('Failed to load vendors');
        } finally {
          setLoadingVendors(false);
        }
      };
      fetchVendors();
    } else {
      // Reset form when switching away from export report tab
      form.resetFields();
      setProductIQEnabled(false);
    }
  }, [activeTab, fetchSDSuppliers, form]);

  /**
   * Fetch the vendor list using loaded settings once AI config settings are loaded
   */
  useEffect(() => {
    if (!settingsLoaded) return;

    const loadFilteredVendors = async () => {
      try {
        await applyContractFilters(settingsFormValues);
      } catch (error) {
        message.error('Failed to load vendor filters');
      }
    };

    loadFilteredVendors();
  }, [settingsLoaded, applyContractFilters]);

  /**
   * Fetch Product IQ jobs when component mounts or tab changes to product
   */
  useEffect(() => {
    if (activeTab === 'product') {
      fetchProductIQJobs(currentPage);
    }
  }, [activeTab, currentPage, pageSize]);

  /**
   * Poll job statuses every 10 seconds for Product IQ tab
   * Batches job IDs in groups of 10 and updates table with latest status
   * Removes completed jobs from the polling batch to stop polling for them
   */
  useEffect(() => {
    let pollingTimeout: NodeJS.Timeout | undefined;
    let currentJobIds = [...jobIdsForPolling];

    if (activeTab === 'product' && currentJobIds.length > 0) {
      const pollJobStatuses = async () => {
        try {
          const statuses = await getDataRefreshJobsStatus(currentJobIds);

          if (!statuses || statuses.length === 0) {
            // Do not schedule next poll if nothing returned
            pollingTimeout = setTimeout(pollJobStatuses, 10000);
            return;
          }

          // Update productIQJobs with the latest status data
          setProductIQJobs((prevJobs) =>
            prevJobs.map((job) => {
              const latestStatus = statuses.find((status) => status.id === job.id);
              if (latestStatus) {
                const successfulInfo = getSuccessfulVendorData(latestStatus);
                const failedInfo = getFailedVendorData(latestStatus, successfulInfo.value);
                return {
                  ...job,
                  status: latestStatus.status,
                  completedAt: latestStatus.completedAt,
                  successfulVendors:
                    typeof successfulInfo.value === 'number' ? successfulInfo.value : job.successfulVendors,
                  failedVendors: typeof failedInfo.value === 'number' ? failedInfo.value : job.failedVendors,
                  failedVendorDetails: latestStatus.failedVendorDetails ?? job.failedVendorDetails,
                  // Update mapped fields
                  supplierOk: typeof successfulInfo.value === 'number' ? successfulInfo.value : job.supplierOk,
                  supplierAlerts: typeof failedInfo.value === 'number' ? failedInfo.value : job.supplierAlerts,
                  // Update product count fields
                  completedProducts:
                    typeof latestStatus.completedProducts === 'number'
                      ? latestStatus.completedProducts
                      : job.completedProducts,
                  failedProducts:
                    typeof latestStatus.failedProducts === 'number' ? latestStatus.failedProducts : job.failedProducts,
                  productOk:
                    typeof latestStatus.completedProducts === 'number' ? latestStatus.completedProducts : job.productOk,
                  productAlerts:
                    typeof latestStatus.failedProducts === 'number' ? latestStatus.failedProducts : job.productAlerts
                };
              }
              return job;
            })
          );

          // Remove completed jobs from polling batch
          const completedJobIds = statuses
            .filter(
              (status) => status.status === 'COMPLETED' || status.status === 'FAILED' || status.status === 'CANCELLED'
            )
            .map((status) => status.id);

          if (completedJobIds.length > 0) {
            currentJobIds = currentJobIds.filter((id) => !completedJobIds.includes(id));
            // Update the state to remove completed jobs from future polling cycles
            setJobIdsForPolling((prevIds) => prevIds.filter((id) => !completedJobIds.includes(id)));
          }

          // Stop polling if no more jobs to poll
          if (currentJobIds.length === 0) {
            return;
          }
        } catch (error) {
          message.error('Error polling job statuses:');
        } finally {
          // Call the next poll AFTER 10 seconds instead of immediately
          if (currentJobIds.length > 0) {
            pollingTimeout = setTimeout(pollJobStatuses, 30000);
          }
        }
      };

      // Start polling after 10 seconds, not immediately
      pollingTimeout = setTimeout(pollJobStatuses, 30000);
    }

    return () => {
      if (pollingTimeout) {
        clearTimeout(pollingTimeout);
      }
    };
  }, [activeTab, jobIdsForPolling, getDataRefreshJobsStatus]);

  /**
   * Handle page change for pagination
   * @param page
   * @param size
   */
  const handlePageChange = (page: number, size?: number) => {
    if (typeof size === 'number' && size !== pageSize) {
      setPageSize(size);
      setCurrentPage(1);
      return;
    }

    setCurrentPage(page);
  };

  /**
   * Handle form submission for export report
   * @param values - Form values
   */
  const handleGenerateReport = async (values: any) => {
    try {
      const reportData = {
        vendorId: values.vendor,
        reportContents: values.reportContents || [],
        productIQ: productIQEnabled,
        productIQSelection: values.productIQSelection || 'all'
      };
      // TODO: Implement actual report generation API call
      message.success("Report generation started. You will be notified when it's ready.");
      form.resetFields();
      setProductIQEnabled(false);
    } catch (err) {
      message.error('Failed to generate report');
    }
  };

  /**
   * Handle save settings button click - filters vendors by contracts and saves settings to API
   * @returns Promise that resolves when the settings have been processed
   */
  const handleSaveSettings = async (): Promise<void> => {
    try {
      const hasVendors = await applyContractFilters(settingsFormValues);
      if (hasVendors) {
        // Save settings to database via API
        await saveAiConfigSettings(settingsFormValues);
        setShowSettings(false);
        setCustomScanSuppliers([]);
        message.success('Settings saved successfully');
      } else {
        message.error('No vendors found matching the criteria');
      }
    } catch (err) {
      message.error('Failed to save settings');
    }
  };

  /**
   * Opens the Custom Scan modal with the provided supplier list
   * @param suppliersOverride - Optional list of suppliers to display; defaults to filtered vendor options
   */
  const openCustomScanModal = useCallback((suppliersOverride?: SupplierOption[]) => {
    if (suppliersOverride && suppliersOverride.length > 0) {
      setCustomScanSuppliers(suppliersOverride);
    } else {
      setCustomScanSuppliers([]);
    }
    setIsCustomScanModalOpen(true);
  }, []);

  /**
   * Closes the Custom Scan modal and resets custom supplier context
   */
  const closeCustomScanModal = useCallback(() => {
    setIsCustomScanModalOpen(false);
    setCustomScanSuppliers([]);
  }, []);

  /**
   * Handle scan all button click
   * Gets vendor IDs from CustomScan modal suppliers (filteredVendors) and triggers API
   */
  const handleScanAll = async () => {
    try {
      const vendorIds = filteredVendors.length > 0 ? filteredVendors.map((v) => v.id) : [];

      if (vendorIds.length === 0) {
        message.warning('No vendors available to scan. Please configure vendors in Custom Scan settings.');
        return;
      }

      // Determine syncType based on active tab
      let syncType: 'PRODUCT_IQ' | 'SUPPLIER_IQ' | 'SUPPLIER_RISK';
      if (activeTab === 'product') {
        syncType = 'PRODUCT_IQ';
      } else if (activeTab === 'supplier') {
        syncType = 'SUPPLIER_IQ';
      } else if (activeTab === 'supplierRisk') {
        syncType = 'SUPPLIER_RISK';
      } else {
        // Default to PRODUCT_IQ if tab is not recognized
        syncType = 'PRODUCT_IQ';
      }

      setIsStartingScan(true);
      setIsScanning(true);
      setScannedSuppliers(0);
      setCollectedProducts(0);

      // Call the data refresh API
      const startResponse = await startVendorDataRefresh({
        syncType,
        vendorIds
      });

      const newJobIds = extractJobIdsFromResponse(startResponse);

      if (newJobIds.length > 0) {
        setJobIdsForPolling((prev) => mergeJobIdLists(prev, newJobIds));
        setCurrentScanJobIds(newJobIds);

        try {
          const newStatuses = await getDataRefreshJobsStatus(newJobIds);

          if (Array.isArray(newStatuses) && newStatuses.length > 0) {
            setProductIQJobs((prevJobs) => {
              const existingIds = new Set(prevJobs.map((job) => job.id));

              const updatedJobs = prevJobs.map((job) => {
                const latestStatus = newStatuses.find((status) => status.id === job.id);
                if (!latestStatus) {
                  return job;
                }
                const successfulInfo = getSuccessfulVendorData(latestStatus);
                const failedInfo = getFailedVendorData(latestStatus, successfulInfo.value);

                return {
                  ...job,
                  status: latestStatus.status,
                  completedAt: latestStatus.completedAt,
                  successfulVendors:
                    typeof successfulInfo.value === 'number' ? successfulInfo.value : job.successfulVendors,
                  failedVendors: typeof failedInfo.value === 'number' ? failedInfo.value : job.failedVendors,
                  failedVendorDetails: latestStatus.failedVendorDetails ?? job.failedVendorDetails,
                  supplierOk: latestStatus.successfulVendors ?? job.supplierOk,
                  supplierAlerts: typeof failedInfo.value === 'number' ? failedInfo.value : job.supplierAlerts
                };
              });

              const newRows: Row[] = newStatuses
                .filter((status) => status.id && !existingIds.has(status.id))
                .map((status) => mapStatusToRow(status, syncType));

              return [...newRows, ...updatedJobs];
            });
          }
        } catch (statusError) {
          message.error('Failed to fetch job status');
        }
      }

      if (activeTab === 'product') {
        await fetchProductIQJobs(currentPage);
      }

      message.success('Scan started successfully');
    } catch (error) {
      message.error('Failed to start scan. Please try again.');
      setIsScanning(false);
    } finally {
      setIsStartingScan(false);
    }
  };

  /**
   * Confirm abort action
   */
  /**
   * Confirm abort action
   */
  const handleConfirmAbort = async () => {
    try {
      if (currentScanJobIds.length > 0) {
        // Cancel only jobs from the current scan
        await Promise.all(
          currentScanJobIds.map((jobId) =>
            cancelDataRefreshJob(jobId).catch((err) => {
              message.error(`Failed to cancel job ${jobId}:`);
              // Continue cancelling other jobs even if one fails
            })
          )
        );
      }

      setIsScanning(false);
      setScannedSuppliers(0);
      setCollectedProducts(0);
      setCurrentScanJobIds([]); // Clear current scan IDs
      message.info('Scan aborted');

      // Refresh the list to show updated statuses
      if (activeTab === 'product') {
        fetchProductIQJobs(currentPage);
      }
    } catch (error) {
      message.error('Failed to abort scan completely');
      // Still stop UI scanning state
      setIsScanning(false);
      setCurrentScanJobIds([]);
    }
  };

  /**
   * Cancel abort action
   */
  const handleCancelAbort = () => {
    // Popconfirm handles its own close state
  };

  /**
   * Handle failed vendors click to open modal with failed vendor details
   * @param record
   */
  const handleFailedVendorsClick = (record: Row) => {
    if (!record.failedVendorDetails || record.failedVendorDetails.length === 0) {
      message.warning('No failed vendor details available for this job');
      return;
    }

    const failedVendors: SupplierOption[] = record.failedVendorDetails.map((vendor) => {
      const fallback =
        filteredVendors.find((fv) => fv.id === vendor.id) ||
        filteredVendors.find((fv) => fv.vendor_name.toLowerCase() === vendor.name.toLowerCase());

      return {
        id: vendor.id,
        name: vendor.name,
        vendor_domain: vendor.vendor_domain || fallback?.vendor_domain
      };
    });

    openCustomScanModal(failedVendors);
  };

  /**
   * Handle table row click to open Edit Product IQ modal
   * @param _record
   */
  const handleTableRowClick = (_record: Row) => {
    openCustomScanModal(filteredVendorOptions);
  };

  /**
   * Generate dynamic summary text based on current contract filter settings
   */
  const generateSummaryTitle = (): string => {
    const { operator, contractCount, timeValue, timeUnit } = settingsFormValues;
    const operatorSymbol = operator === 'gte' ? 'more than/equal to' : 'less than/equal to';
    return `Configured to scan and update on all vendors with ${operatorSymbol} ${contractCount} contracts in the last ${timeValue} ${timeUnit.toLowerCase()}`;
  };

  /**
   * Generate "Last run record" text from the first job in the table
   */
  const generateLastRunRecord = (): string => {
    if (!productIQJobs || productIQJobs.length === 0) {
      return 'No run records available';
    }

    const lastJob = productIQJobs[0];
    const startDate = new Date(lastJob.startTime);
    const now = new Date();
    const daysAgo = Math.floor((now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));

    const formattedDate = startDate.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });

    return `Last run record — ${formattedDate} (${daysAgo} days ago) by ${lastJob.createdByName || 'system'}`;
  };

  const summary =
    activeTab === 'product'
      ? {
          mainTitle: generateSummaryTitle(),
          title: generateLastRunRecord(),
          supplierRecords: productIQJobs[0]?.successfulVendors ?? 0,
          productCollected: productIQJobs[0]?.completedProducts ?? 0
        }
      : activeTab === 'supplier'
      ? {
          mainTitle: generateSummaryTitle(),
          title: generateLastRunRecord(),
          supplierRecords: 0,
          productCollected: 0
        }
      : activeTab === 'supplierRisk'
      ? {
          mainTitle: generateSummaryTitle(),
          title: generateLastRunRecord(),
          supplierRecords: 0,
          productCollected: 0
        }
      : {
          mainTitle: generateSummaryTitle(),
          title: generateLastRunRecord(),
          supplierRecords: 0,
          productCollected: 0
        };

  return (
    <div className="min-h-screen ">
      <h1>AI Configuration </h1>
      <Layout className="!bg-transparent gap-8 !flex-col lg:!flex-row">
        {/* Sidebar Tabs */}
        <div className="w-[200px] md:w-[220px] lg:w-[220px] xl:w-[320px]">
          <Sider width={'100%'} className="!bg-transparent">
            <Card className="rounded-2xl shadow-sm" bodyStyle={{ padding: 16 }}>
              <div className="flex flex-col gap-4">
                <SidebarTab
                  icon={<AppstoreOutlined style={{ fontSize: 20 }} />}
                  label="Product IQ"
                  isActive={activeTab === 'product'}
                  onClick={() => setActiveTab('product')}
                />
                {/* <SidebarTab
                  icon={<BankOutlined style={{ fontSize: 20 }} />}
                  label="Supplier IQ"
                  isActive={activeTab === "supplier"}
                  onClick={() => setActiveTab("supplier")}
                />
                <SidebarTab
                  icon={<WarningOutlined style={{ fontSize: 20 }} />}
                  label="Supplier Risk"
                  isActive={activeTab === "supplierRisk"}
                  onClick={() => setActiveTab("supplierRisk")}
                /> */}
                {/* <SidebarTab
                  icon={<ExportOutlined style={{ fontSize: 20 }} />}
                  label="Export Report"
                  isActive={activeTab === "exportReport"}
                  onClick={() => setActiveTab("exportReport")}
                /> */}
              </div>
            </Card>
          </Sider>
        </div>

        {/* Main Content */}
        <Layout className="!bg-white p-8 border border-gray-200 rounded-xl shadow-[0_0_20px_0_#0000000D]  !w-full">
          <Content>
            {/* <TabContent
              title={activeTab === "product" ? "Product IQ" : "Supplier IQ"}
              summary={summary}
              data={currentData}
              icon={activeTab  === "product" ? <AppstoreOutlined style={{ fontSize: 20 }}/> : <BankOutlined style={{ fontSize: 20 }} /> }
            /> */}
            <TabContent
              title={
                activeTab === 'product'
                  ? 'Product IQ'
                  : activeTab === 'supplier'
                  ? 'Supplier IQ'
                  : activeTab === 'supplierRisk'
                  ? 'Supplier Risk'
                  : 'Export Report'
              }
              summary={summary}
              data={currentData}
              icon={
                activeTab === 'product' ? (
                  <AppstoreOutlined />
                ) : activeTab === 'supplier' ? (
                  <BankOutlined />
                ) : activeTab === 'supplierRisk' ? (
                  <WarningOutlined />
                ) : (
                  <ExportOutlined />
                )
              }
              showSettings={showSettings}
              toggleSettings={() => setShowSettings(!showSettings)}
              form={form}
              vendors={vendors}
              loadingVendors={loadingVendors}
              productIQEnabled={productIQEnabled}
              setProductIQEnabled={setProductIQEnabled}
              onGenerateReport={handleGenerateReport}
              isScanning={isScanning}
              scannedSuppliers={scannedSuppliers}
              collectedProducts={collectedProducts}
              onScanAll={handleScanAll}
              onConfirmAbort={handleConfirmAbort}
              onCancelAbort={handleCancelAbort}
              onTableRowClick={handleTableRowClick}
              onFailedVendorsClick={handleFailedVendorsClick}
              onCustomScanClick={() => openCustomScanModal()}
              onSaveSettings={handleSaveSettings}
              loadingFilteredVendors={loadingFilteredVendors}
              settingsFormValues={settingsFormValues}
              setSettingsFormValues={setSettingsFormValues}
              currentPage={currentPage}
              pageSize={pageSize}
              onPageChange={handlePageChange}
              pageSizeOptions={[5, 10, 20, 50, 100]}
              loadingJobs={loadingJobs}
              paginationInfo={paginationInfo}
              hasVendors={filteredVendors.length > 0}
            />
          </Content>
        </Layout>
      </Layout>
      <CustomScanModal
        open={isCustomScanModalOpen}
        onCancel={closeCustomScanModal}
        suppliers={customScanSuppliers.length > 0 ? customScanSuppliers : filteredVendorOptions}
        onRefreshComplete={() => {
          // Refresh the jobs table to show the newly started job
          if (activeTab === 'product') {
            fetchProductIQJobs(currentPage);
          }
        }}
      />
    </div>
  );
}

/* -------- Helper Components -------- */

const SidebarTab = ({
  icon,
  label,
  isActive,
  onClick
}: {
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
  onClick: () => void;
}) => (
  <div
    onClick={onClick}
    className={`flex items-center font-semibold gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
      isActive ? ' text-[#0070FF]' : 'text-gray-700 hover:bg-gray-50'
    }`}
  >
    {icon}
    <Text className={`text-base ${isActive ? ' text-[#0070FF]' : 'text-gray-700'}`}>{label}</Text>
  </div>
);

/**
 * AbortButton component with confirmation modal
 * @param onConfirm.onConfirm
 * @param onConfirm - Callback when user confirms abort
 * @param onCancel - Callback when user cancels abort
 * @param onConfirm.onCancel
 */
const AbortButton = ({ onConfirm, onCancel }: { onConfirm?: () => void; onCancel?: () => void }) => {
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

  const handleOpenModal = () => setIsModalVisible(true);

  const handleConfirm = () => {
    setIsModalVisible(false);
    onConfirm?.();
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    onCancel?.();
  };

  return (
    <>
      <Button
        type="default"
        className="flex text-white items-center gap-2 px-4 py-4 h-auto bg-[#ff0000] border-[#ff0000]"
        style={{ backgroundColor: '#ff0000', borderColor: '#ff0000', color: '#ffffff' }}
        onClick={handleOpenModal}
      >
        <UpOutlined style={{ fontSize: 12 }} />
        Abort
      </Button>
      <Modal
        title="Abort scan?"
        open={isModalVisible}
        onOk={handleConfirm}
        onCancel={handleCancel}
        okText="Yes"
        cancelText="Cancel"
        okButtonProps={{ danger: true }}
      >
        <div className="text-gray-700">
          <div className="font-semibold mb-2">Are you sure you want to abort?</div>
          <div className="text-sm text-gray-500">All progress will be lost and this action cannot be undone.</div>
        </div>
      </Modal>
    </>
  );
};

const TabContent = ({
  title,
  summary,
  data,
  icon,
  showSettings,
  toggleSettings,
  form,
  vendors,
  loadingVendors,
  productIQEnabled,
  setProductIQEnabled,
  onGenerateReport,
  isScanning,
  scannedSuppliers,
  collectedProducts,
  onScanAll,
  onConfirmAbort,
  onCancelAbort,
  onTableRowClick,
  onFailedVendorsClick,
  loadingJobs,
  paginationInfo,
  onCustomScanClick,
  onSaveSettings,
  loadingFilteredVendors,
  settingsFormValues,
  setSettingsFormValues,
  currentPage,
  pageSize,
  onPageChange,
  pageSizeOptions,
  hasVendors
}: {
  title: string;
  summary: any;
  data: Row[];
  icon: React.ReactNode;
  showSettings: boolean;
  toggleSettings: () => void;
  form?: any;
  vendors?: SupplierDynamicsVendor[];
  loadingVendors?: boolean;
  productIQEnabled?: boolean;
  setProductIQEnabled?: (value: boolean) => void;
  onGenerateReport?: (values: any) => void;
  isScanning?: boolean;
  scannedSuppliers?: number;
  collectedProducts?: number;
  onScanAll?: () => void;
  onConfirmAbort?: () => void;
  onCancelAbort?: () => void;
  onTableRowClick?: (record: Row) => void;
  onFailedVendorsClick?: (record: Row) => void;
  onCustomScanClick?: () => void;
  onSaveSettings?: () => void;
  loadingFilteredVendors?: boolean;
  settingsFormValues?: ContractFilterSettings;
  setSettingsFormValues?: React.Dispatch<React.SetStateAction<ContractFilterSettings>>;
  currentPage?: number;
  pageSize?: number;
  onPageChange?: (page: number, pageSize?: number) => void;
  pageSizeOptions?: number[];
  loadingJobs?: boolean;
  paginationInfo?: PaginationInfo | null;
  hasVendors?: boolean;
}) => {
  const isProductIQ = title === 'Product IQ';
  // For Product IQ, data is already paginated from API, so use as-is
  // For other tabs, use local pagination if needed
  const paginatedData = isProductIQ
    ? data
    : currentPage && pageSize
    ? data.slice((currentPage - 1) * pageSize, currentPage * pageSize)
    : data;

  return (
    <>
      {/* Header Row */}
      <div className="flex items-center justify-between mb-4 border-b border-gray-300 pb-4">
        <div className="flex items-center gap-3 text-xl">
          {icon}
          <Title level={3} style={{ margin: 0, fontWeight: 500 }}>
            {title}
          </Title>
        </div>

        {title !== 'Export Report' && title !== 'Supplier Risk' && (
          <Button onClick={toggleSettings} className="!border-none !shadow-none !text-[#0070FF] font-medium text-base">
            <SettingFilled style={{ fontSize: 18 }} />
            {showSettings ? 'Close Settings' : 'Edit Settings'}
          </Button>
        )}
      </div>

      <div className="main-setting !bg-[#F8F8F8] rounded-xl">
        {/* ▼▼ Slide-down Settings Panel ▼▼ */}
        <div
          className="overflow-hidden transition-all duration-300 "
          style={{
            maxHeight: showSettings ? '202px' : '0px',
            opacity: showSettings ? 1 : 0
          }}
        >
          <Card className=" bg-[#F8F8F8] border-none !shadow-none" bodyStyle={{ padding: 20 }}>
            <h2>Scan and update on all vendor with...</h2>
            <div className="border border-b pb-5">
              <div className="flex items-center gap-2 flex-wrap  pb-5">
                <span>contracts</span>
                <Select
                  value={settingsFormValues?.operator || 'gte'}
                  onChange={(value) =>
                    setSettingsFormValues?.((prev) => ({ ...prev, operator: value as 'gte' | 'lte' }))
                  }
                  style={{ width: 80 }}
                >
                  <Option value="gte">&gt;=</Option>
                  <Option value="lte">&lt;=</Option>
                </Select>

                <InputNumber
                  min={0}
                  value={settingsFormValues?.contractCount || 5}
                  onChange={(value) => setSettingsFormValues?.((prev) => ({ ...prev, contractCount: value || 0 }))}
                  style={{ width: 80 }}
                />

                <span>in the last</span>

                <InputNumber
                  min={1}
                  value={settingsFormValues?.timeValue || 6}
                  onChange={(value) => setSettingsFormValues?.((prev) => ({ ...prev, timeValue: value || 1 }))}
                  style={{ width: 80 }}
                />

                <Select
                  value={settingsFormValues?.timeUnit || 'Months'}
                  onChange={(value) =>
                    setSettingsFormValues?.((prev) => ({
                      ...prev,
                      timeUnit: value as 'Days' | 'Weeks' | 'Months' | 'Years'
                    }))
                  }
                  style={{ width: 110 }}
                >
                  <Option value="Days">Days</Option>
                  <Option value="Weeks">Weeks</Option>
                  <Option value="Months">Months</Option>
                  <Option value="Years">Years</Option>
                </Select>

                {/* <span>every</span>

              <InputNumber min={1} defaultValue={15} style={{ width: 80 }} />

              <Select defaultValue="Days" style={{ width: 110 }}>
                <Option value="Days">Days</Option>
                <Option value="Weeks">Weeks</Option>
              </Select> */}
              </div>
              <Button
                onClick={onSaveSettings}
                type="primary"
                className="mt-4 text-base py-2 !h-auto"
                loading={loadingFilteredVendors}
              >
                <SaveFilled />
                Save Settings
              </Button>
            </div>
          </Card>
        </div>
        {/* ▲▲ End Panel ▲▲ */}
        <Card className="rounded-xl mb-8 !shadow-none !bg-[#F8F8F8] border-none" bodyStyle={{ padding: 20 }}>
          <div className="flex justify-between items-start flex-wrap gap-2">
            {title !== 'Export Report' && !isScanning && (
              <div className="block">
                <h2>{summary.mainTitle}</h2>
                <Title level={4} style={{ marginBottom: 6, fontSize: '14px', color: '#404040' }}>
                  {summary.title}
                </Title>

                <div className="flex items-center text-sm text-gray-600 flex-wrap gap-2">
                  <Text className="divider-main pr-3 mr-3">
                    <Text strong>Scanned Supplier records</Text> : {summary.supplierRecords}
                  </Text>
                  <Text>
                    <Text strong>Product info collected</Text> : {summary.productCollected.toLocaleString()}
                  </Text>
                  {/* <a className="text-blue-600 cursor-pointer flex gap-1 items-center">
                <img src={DownloadIcon} alt="Download" style={{ width: 16, height: 16 }} />
                <span className="font-medium">Download system logs</span>
              </a> */}
                </div>

                {/* <div className="mt-3 text-sm text-gray-500">{summary.nextRun}</div> */}
              </div>
            )}
            {title !== 'Export Report' && isScanning && (
              <div className="block w-full">
                <h2 className="mb-4 text-base font-normal">{summary.mainTitle}</h2>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-[#4FD1C7] flex items-center justify-center scanning-icon-container">
                    <RobotOutlined style={{ fontSize: 24, color: 'white' }} />
                  </div>
                  <div className="flex-1">
                    <Text className="text-base font-medium block mb-1">Scanning in progress</Text>
                    <div className="flex items-center text-sm text-gray-600 gap-2">
                      <Text>
                        <Text strong>Scanned Supplier records</Text>: {scannedSuppliers}
                      </Text>
                      <span className="text-gray-300">|</span>
                      <Text>
                        <Text strong>Product info collected</Text>: {collectedProducts}
                      </Text>
                    </div>
                  </div>
                  <AbortButton onConfirm={onConfirmAbort} onCancel={onCancelAbort} />
                </div>
              </div>
            )}
          </div>

          {/* Horizontal divider */}
          {title !== 'Export Report' && !isScanning && (
            <div style={{ borderTop: '1px solid #E6E6E6', marginTop: 18, paddingTop: 12 }}>
              <div className="flex items-center">
                {/* Scan All */}
                <Button
                  type="link"
                  className="flex items-center  pl-0 text-base font-medium text-[#0070FF]"
                  onClick={onScanAll}
                  disabled={!hasVendors || loadingFilteredVendors}
                >
                  <SearchOutlined style={{ fontSize: 16 }} />
                  <span className="font-medium">Scan All</span>
                </Button>

                {/* vertical divider */}
                <div style={{ height: 18, width: 1, background: '#E6E6E6', margin: '0 8px' }} />

                {/* Custom Scan */}
                <Button
                  type="link"
                  className="flex items-center text-base font-medium text-[#0070FF]"
                  onClick={onCustomScanClick}
                  disabled={!hasVendors || loadingFilteredVendors}
                >
                  <SettingFilled style={{ fontSize: 18 }} />
                  <span className="font-medium">
                    {title === 'Supplier Risk' || title === 'Supplier IQ' ? 'Update Content' : 'Custom Scan'}
                  </span>
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>

      {title !== 'Export Report' && title !== 'Supplier Risk' && title !== 'Supplier IQ' && (
        <>
          <div className="rounded-xl shadow-sm custom-table">
            <Table<Row>
              columns={getColumns(onTableRowClick ?? ((_record: Row) => undefined), onFailedVendorsClick)}
              loading={loadingJobs}
              dataSource={paginatedData}
              pagination={false}
              rowClassName={() => 'align-middle'}
            />
          </div>
          {isProductIQ && onPageChange && paginationInfo && (
            <div className="flex justify-end mt-4">
              <Pagination
                current={paginationInfo.pageNumber}
                pageSize={paginationInfo.pageSize ?? pageSize}
                total={paginationInfo.totalElements}
                onChange={onPageChange}
                showSizeChanger
                pageSizeOptions={(pageSizeOptions ?? [5, 10, 15]).map((size) => size.toString())}
                showQuickJumper={false}
                showTotal={(total, range) => `${range[0]}-${range[1]} of ${total}`}
              />
            </div>
          )}
        </>
      )}
    </>
  );
};
